import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input, TextArea } from '@/components/ui/Input';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { Combobox } from '@/components/ui/Combobox';
import { toast } from '@/components/ui/Toast';
import { invoicesApi } from '@/services/endpoints/transactions';
import { shopsApi } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import { addDays, today } from '@/lib/dateUtils';
import { formatMoney } from '@/domain/money';
import type { Invoice } from '@/domain/types';

interface FormState {
  shopId: number | null;
  invoiceNo: string;
  description: string;
  amountCents: number;
  invoiceDate: string;
  dueDate: string;
}

export function InvoiceFormModal({
  isOpen,
  onClose,
  invoice,
}: {
  isOpen: boolean;
  onClose: () => void;
  invoice?: Invoice | null;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>({
    shopId: null,
    invoiceNo: '',
    description: '',
    amountCents: 0,
    invoiceDate: today(),
    dueDate: addDays(today(), 30),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [routedTo, setRoutedTo] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const allocatedCents = invoice?.allocatedCents ?? 0;
  const amountChanged = Boolean(invoice) && form.amountCents !== invoice?.amountCents;
  const belowFloor = allocatedCents > 0 && form.amountCents < allocatedCents;

  // The server allows a move only while nothing has ever been collected; it
  // also refuses one with a voided receipt, which this view cannot see.
  const canMoveShop = Boolean(invoice) && invoice?.status === 'OPEN' && allocatedCents === 0;
  const shopChanged = Boolean(invoice) && form.shopId !== invoice?.shopId;
  const needsReason = (allocatedCents > 0 && amountChanged) || shopChanged;

  useEffect(() => {
    if (!isOpen) return;

    setErrors({});
    setReason('');
    setRoutedTo(invoice?.agentName ?? null);
    setForm(
      invoice
        ? {
            shopId: invoice.shopId,
            invoiceNo: invoice.invoiceNo,
            description: invoice.description ?? '',
            amountCents: invoice.amountCents,
            invoiceDate: invoice.invoiceDate ?? today(),
            dueDate: invoice.dueDate,
          }
        : {
            shopId: null,
            invoiceNo: '',
            description: '',
            amountCents: 0,
            invoiceDate: today(),
            dueDate: addDays(today(), 30),
          },
    );
  }, [isOpen, invoice]);

  const mutation = useMutation({
    mutationFn: () =>
      invoice
        ? invoicesApi.update(invoice.id, {
            ...(shopChanged ? { shopId: form.shopId! } : {}),
            description: form.description || null,
            amountCents: form.amountCents,
            invoiceDate: form.invoiceDate,
            dueDate: form.dueDate,
            ...(needsReason ? { reason: reason.trim() } : {}),
          })
        : invoicesApi.create({
            shopId: form.shopId!,
            invoiceNo: form.invoiceNo.trim(),
            description: form.description || null,
            amountCents: form.amountCents,
            invoiceDate: form.invoiceDate,
            dueDate: form.dueDate,
          }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.shops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      toast.success(invoice ? t('invoice.updated') : t('invoice.created'));

      // Confirm the automatic routing back to the user — this is the mapping
      // the requirements ask for, and it is invisible unless it is stated.
      if ((!invoice || shopChanged) && saved.agentName) {
        toast.info(`${t('invoice.routedTo')}: ${saved.agentName} · ${saved.routeName ?? ''}`);
      }

      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.fieldErrors.length > 0) {
        setErrors(Object.fromEntries(error.fieldErrors.map((issue) => [issue.field, issue.message])));
      } else {
        toast.error(error instanceof ApiError ? error.message : t('errors.unknown'));
      }
    },
  });

  const isValid =
    !belowFloor &&
    (!needsReason || reason.trim().length >= 3) &&
    ((invoice !== null && invoice !== undefined && form.shopId !== null) ||
      (form.shopId !== null && form.invoiceNo.trim().length > 0 && form.amountCents > 0 && form.dueDate));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={invoice ? t('invoice.edit') : t('invoice.new')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending} disabled={!isValid}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* The shop drives the automatic route and agent mapping. */}
        <Combobox
          label={t('invoice.customer')}
          value={form.shopId}
          initialLabel={invoice?.shopName ?? ''}
          disabled={Boolean(invoice) && !canMoveShop}
          onChange={(id, option) => {
            setForm((previous) => ({ ...previous, shopId: id }));
            setRoutedTo(option?.sublabel ?? null);
          }}
          onSearch={async (term) => {
            const results = await shopsApi.suggest(term);
            return results.map((shop) => ({
              id: shop.id,
              label: shop.name,
              sublabel: shop.routeName ?? t('shop.noRoute'),
            }));
          }}
          error={errors.shopId}
          required
        />

        {routedTo && (
          <p className="rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-800">
            {t('invoice.routedTo')}: {routedTo}
          </p>
        )}

        {shopChanged && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {t('invoice.moveShopNotice')}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('invoice.invoiceNo')}
            value={form.invoiceNo}
            onChange={(event) => setForm({ ...form, invoiceNo: event.target.value })}
            error={errors.invoiceNo}
            disabled={Boolean(invoice)}
            required
          />

          <MoneyInput
            label={t('invoice.amount')}
            value={form.amountCents}
            onChange={(cents) => setForm({ ...form, amountCents: cents })}
            error={
              belowFloor
                ? t('invoice.editFloorNotice', { amount: formatMoney(allocatedCents) })
                : errors.amountCents
            }
            required
          />
        </div>

        {invoice && allocatedCents > 0 && !belowFloor && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {t('invoice.editFloorNotice', { amount: formatMoney(allocatedCents) })}
          </p>
        )}

        {needsReason && (
          <TextArea
            label={t('invoice.editReasonLabel')}
            hint={shopChanged ? t('invoice.moveReasonHint') : t('invoice.editReasonHint')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
          />
        )}

        <TextArea
          label={t('invoice.description')}
          value={form.description}
          onChange={(event) => setForm({ ...form, description: event.target.value })}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('invoice.invoiceDate')}
            type="date"
            value={form.invoiceDate}
            onChange={(event) => setForm({ ...form, invoiceDate: event.target.value })}
          />
          <Input
            label={t('invoice.dueDate')}
            type="date"
            value={form.dueDate}
            onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
            error={errors.dueDate}
            required
          />
        </div>
      </div>
    </Modal>
  );
}
